import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUp, Maximize2 } from "lucide-react";
import { asset } from "@/lib/api";

const Btn = ({ testid, children, ...p }) => (
  <button data-testid={testid} {...p}
    className="h-10 px-3 flex items-center gap-2 border border-[#F5A524]/40 text-[#F5A524] font-mono text-[11px] tracking-[0.2em] hover:bg-[#F5A524] hover:text-black disabled:opacity-30 disabled:pointer-events-none transition-colors">
    {children}
  </button>
);

export const WebtoonReader = ({ episode, episodes, onSelect }) => {
  const frame = useRef(null);
  const root = useRef(null);
  const [progress, setProgress] = useState(0);
  const idx = episodes.findIndex((e) => e.id === episode.id);
  const prev = episodes[idx - 1];
  const next = episodes[idx + 1];

  useEffect(() => {
    frame.current?.scrollTo({ top: 0 });
    setProgress(0);
  }, [episode.id]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    setProgress(max > 0 ? el.scrollTop / max : 0);
  };

  const full = () => (document.fullscreenElement ? document.exitFullscreen() : root.current?.requestFullscreen?.());

  return (
    <div ref={root} data-testid="webtoon-reader" className="mx-auto w-full max-w-[720px] bg-[#0B0C10] flex flex-col">
      <div className="flex items-center justify-between gap-3 border border-[#F5A524]/30 border-b-0 px-4 py-3 font-mono text-[11px] tracking-[0.2em]">
        <span className="text-[#F5A524] truncate">EP {String(episode.number).padStart(2, "0")} // {episode.title || "UNTITLED"}</span>
        <span data-testid="webtoon-progress-label" className="text-zinc-400 shrink-0">{Math.round(progress * 100)}%</span>
      </div>
      <div className="h-1 bg-white/10"><div className="h-full bg-[#F5A524] origin-left" style={{ transform: `scaleX(${progress})` }} /></div>
      <div ref={frame} onScroll={onScroll} data-lenis-prevent data-testid="webtoon-frame"
        className="h-[min(72vh,calc(100vh-250px))] min-h-[420px] [:fullscreen_&]:h-[calc(100vh-110px)] overflow-y-auto overscroll-contain border-x border-[#F5A524]/30 bg-black">
        {(episode.pages ?? []).map((p, i) => (
          <img key={`${p}-${i}`} src={asset(p)} alt={`Panel ${i + 1}`} loading={i < 2 ? "eager" : "lazy"} className="block w-full h-auto select-none" draggable={false} />
        ))}
        {(episode.pages ?? []).length === 0 && <p className="p-10 font-mono text-sm text-zinc-400">NO PANELS IN THIS EPISODE YET.</p>}
        <div className="py-14 text-center border-t border-[#F5A524]/20">
          <p className="font-mono text-[11px] tracking-[0.35em] text-zinc-400">END OF EPISODE {String(episode.number).padStart(2, "0")}</p>
          {next && (
            <button data-testid="webtoon-end-next-btn" onClick={() => onSelect(next)} className="mt-5 clip-tab bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] px-6 py-3 hover:bg-white transition-colors">
              NEXT: EP {String(next.number).padStart(2, "0")} →
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border border-[#F5A524]/30 border-t-0 px-3 py-3">
        <div className="flex gap-2">
          <Btn testid="webtoon-prev-ep-btn" disabled={!prev} onClick={() => onSelect(prev)}><ChevronLeft size={14} /> PREV</Btn>
          <Btn testid="webtoon-next-ep-btn" disabled={!next} onClick={() => onSelect(next)}>NEXT <ChevronRight size={14} /></Btn>
        </div>
        <div className="flex gap-2">
          <Btn testid="webtoon-top-btn" onClick={() => frame.current?.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top"><ArrowUp size={14} /></Btn>
          <Btn testid="webtoon-fullscreen-btn" onClick={full} aria-label="Fullscreen"><Maximize2 size={14} /></Btn>
        </div>
      </div>
    </div>
  );
};
